import type { HierarchyRectangularNode } from 'd3-hierarchy';

import type { Overlay } from '../../core/components/overlay-tooltip/types.js';
import {
  useOverlayEventHandlers,
  type Options,
} from '../../core/hooks/useOverlayEventHandlers.js';
import { LABELS_DISPLAY } from '../constants.js';
import { useFormatter } from '../context/FormatterContext.js';
import { useLabelsDisplay } from '../context/LabelsDisplayContext.js';
import { useTreeMapInternalData } from '../context/TreeMapInternalDataContext.js';
import { useClusterNodesState, useLeafNodesState } from '../store/selectors.js';
import type { InternalTreeMapNode } from '../types/internal.js';
import { buildOverlayTooltipData } from '../utils/build-overlay-tooltip-data.js';
import { getTooltipData } from '../utils/get-tooltip-data.js';

export const useOverlayHandlers = (overlay: Overlay) => {
  const hierarchyLeafNodes = useLeafNodesState();
  const hierarchyClusterNodes = useClusterNodesState();
  const labelsDisplay = useLabelsDisplay();
  const formatter = useFormatter();
  const { unit } = useTreeMapInternalData();

  const parentLabels: string[] = [LABELS_DISPLAY.All, LABELS_DISPLAY.Clusters];
  const hasParentLabels = parentLabels.includes(labelsDisplay);

  const getTooltipDataFromEvent: Options['getTooltipData'] = (coords) => {
    const position = overlay.getAdjustedPositionsFromAbsolute(
      coords.clientX,
      coords.clientY,
    );
    const relativePosition = {
      x: position.relativeX ?? 0,
      y: position.relativeY ?? 0,
    };
    const tooltipData = getTooltipData(
      hierarchyLeafNodes,
      hierarchyClusterNodes as HierarchyRectangularNode<InternalTreeMapNode>[],
      relativePosition,
      hasParentLabels,
    );
    return tooltipData
      ? buildOverlayTooltipData(tooltipData, formatter, unit)
      : undefined;
  };

  return useOverlayEventHandlers({
    getTooltipData: getTooltipDataFromEvent,
  });
};
